/**
 * External identity-provider federation (human sign-in) wire shapes + form schemas.
 *
 * PureID is the relying party on the customer's HUMAN IdP: a tenant admin registers
 * the external OIDC providers it trusts so people sign in with their existing
 * corporate accounts ("Sign in with Google/Microsoft/your OIDC IdP"). This is the
 * human-identity sibling of the agent "trusted workload issuer" registry — same
 * relying-party trust model, deliberately DIFFERENT vocabulary (never "trusted
 * issuer" / "agent" here).
 *
 * Backs the canonical inbound-federation system (internal/identity/federation):
 *   GET    /api/v1/tenants/{tenant_id}/federation                list
 *   POST   /api/v1/tenants/{tenant_id}/federation                create
 *   PUT    /api/v1/tenants/{tenant_id}/federation/{provider_id}  update
 *   DELETE /api/v1/tenants/{tenant_id}/federation/{provider_id}  delete
 *
 * It is OIDC-only by design (Google / Microsoft Entra / generic OIDC). The
 * client_secret is write-only — responses carry no secret material.
 */

import { z } from "zod";

// Read model accepts every persisted type (incl. apple, which exists in the model
// but cannot be CREATED yet — the backend rejects apple creation). The create flow
// below only offers the supported types, so we never submit one that 422s.
export const providerTypeSchema = z.enum(["google", "microsoft", "apple", "oidc_generic"]);
export type ProviderType = z.infer<typeof providerTypeSchema>;

const providerTestDiagnosticSchema = z.object({
  provider_revision: z.number().int().positive(),
 correlation_id: z.string().uuid(),
  status: z.enum(["authorized", "in_progress", "succeeded", "failed"]),
  stage: z.enum(["authorization", "redirect", "callback", "token", "claims", "account_resolution", "complete"]),
  failure_code: z.string().max(64).optional(),
  started_at: z.string().datetime(),
  completed_at: z.string().datetime().optional(),
});

// Exact signed-claim requirements; absent is off. Never infer strength from names.
const trustValues = (maxBytes: number) => z.array(z.string().min(1).refine(
  (value) => new TextEncoder().encode(value).length <= maxBytes && !/[\s\p{Cc}]/u.test(value),
  "Use a bounded claim value without whitespace or control characters.",
)).max(16).refine((values) => new Set(values).size === values.length, "Remove duplicate values.");
export const federationTrustPolicySchema = z.object({
  accepted_acr: trustValues(1024),
  required_amr: trustValues(128),
  max_age_seconds: z.number().int().min(60).max(86400),
}).strict().refine((policy) => policy.accepted_acr.length + policy.required_amr.length > 0,
  "Supply at least one accepted ACR value or required AMR value.");
export type FederationTrustPolicy = z.infer<typeof federationTrustPolicySchema>;

// ── read model (adminProviderRow) ───────────────────────────────────────────
export const federationProviderSchema = z.object({
  version: z.number().int().positive(),
  configuration_revision: z.number().int().positive(),
  serving_revision: z.number().int().positive().nullable(),
  tested_revision: z.number().int().positive().nullable(),
  id: z.string(),
  provider_type: providerTypeSchema,
  display_name: z.string(),
  client_id: z.string().optional().default(""),
  // Owning organization (absent = tenant-wide, the workforce shape).
  organization_id: z.string().optional(),
  // The identity source this tenant-wide provider is the sign-in half of
  //; absent when unlinked or an organization's provider.
  source_id: z.string().optional(),
  discovery_url: z.string().optional().default(""),
  scopes: z.array(z.string()).optional().default([]),
  enabled: z.boolean().optional().default(false),
  require_email_verified: z.boolean().optional().default(true),
  trust_policy: federationTrustPolicySchema.nullable().optional(),
  allow_application_access: z.boolean().optional(),
  last_successful_signin_at: z.string().datetime().optional(),
  last_test: providerTestDiagnosticSchema.optional(),
});
export type FederationProvider = z.infer<typeof federationProviderSchema>;

export function providersForOrganization(
  providers: FederationProvider[],
  organizationID?: string,
): FederationProvider[] {
  return providers.filter(
    (provider) => organizationID === undefined || provider.organization_id === organizationID,
  );
}

export const federationProvidersResponseSchema = z.object({
  items: z.array(federationProviderSchema).optional().default([]),
});

export const providerTestGrantSchema = z.object({
 provider_revision: z.number().int().positive(),
  token: z.string().min(32).max(128),
  organization_id: z.string().uuid(),
  provider_id: z.string().uuid(),
  correlation_id: z.string().uuid(),
  expires_at_unix: z.number().int(),
});
export type FederationProvidersResponse = z.infer<typeof federationProvidersResponseSchema>;

// ── validators ──────────────────────────────────────────────────────────────
const LOOPBACK_HOSTS = new Set(["localhost", "127.0.0.1", "::1"]);

function isHttpsURL(raw: string): boolean {
  if (raw.trim() !== raw || /\s/.test(raw)) return false;
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return false;
  }
  if (url.username || url.password) return false;
  if (url.protocol === "https:") return true;
  return url.protocol === "http:" && LOOPBACK_HOSTS.has(url.hostname);
}

// ── create / update input ────────────────────────────────────────────────────
// provider_type is chosen via a preset; the discriminator is the type, not a
// protocol. Microsoft + generic OIDC REQUIRE a discovery URL (the backend refuses
// Microsoft without one — the /common/ default accepts any account — and generic
// OIDC has no well-known default); Google auto-defaults its discovery endpoint.
export const createFederationProviderSchema = z
  .object({
    organization_id: z.string().uuid().optional(),
    source_id: z.string().uuid().optional(),
    provider_type: z.enum(["google", "microsoft", "oidc_generic"]),
    display_name: z.string().min(1, "A display name is required"),
    client_id: z.string().min(1, "Client ID is required"),
    client_secret: z.string().optional(),
    discovery_url: z.string().optional(),
    scopes: z.array(z.string().min(1)).optional(),
    enabled: z.boolean(),
    require_email_verified: z.boolean(),
    trust_policy: federationTrustPolicySchema.nullable().optional(),
  allow_application_access: z.boolean().optional(),
  })
  .superRefine((val, ctx) => {
    if (val.allow_application_access && !val.trust_policy) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["trust_policy"], message: "Application access requires explicit upstream authentication requirements." });
    const needsDiscovery = val.provider_type === "microsoft" || val.provider_type === "oidc_generic";
    const discovery = val.discovery_url ?? "";
    if (needsDiscovery && discovery === "") {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["discovery_url"],
        message:
          val.provider_type === "microsoft"
            ? "Microsoft requires the tenant-specific discovery URL (the /common/ endpoint accepts any account)."
            : "A discovery URL is required for a generic OIDC provider.",
      });
    }
    if (discovery !== "" && !isHttpsURL(discovery)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["discovery_url"],
        message: "Discovery URL must be a valid https URL.",
      });
    }
  });
export type CreateFederationProviderInput = z.infer<typeof createFederationProviderSchema>;

export const federationSearchSchema = z.object({
  organizationId: z.string().uuid().optional(),
  add: z.boolean().optional(),
});

// ── provider presets (UI-only; prefill type + discovery hints) ───────────────
// These name a customer's HUMAN IdP, not a vertical — generic + safe. Apple is
// intentionally omitted: the backend does not yet support creating Apple providers.
export type FederationPreset = "google" | "microsoft" | "oidc_generic";
export interface FederationPresetMeta {
  value: FederationPreset;
  label: string;
  /** Whether a discovery URL must be supplied (Google auto-defaults it). */
  needsDiscovery: boolean;
  discoveryPlaceholder: string;
}
export const FEDERATION_PRESETS: FederationPresetMeta[] = [
  {
    value: "google",
    label: "Google Workspace",
    needsDiscovery: false,
    discoveryPlaceholder: "https://accounts.google.com/.well-known/openid-configuration (auto)",
  },
  {
    value: "microsoft",
    label: "Microsoft Entra ID",
    needsDiscovery: true,
    discoveryPlaceholder:
      "https://login.microsoftonline.com/<tenant>/v2.0/.well-known/openid-configuration",
  },
  {
    value: "oidc_generic",
    label: "Generic OIDC",
    needsDiscovery: true,
    discoveryPlaceholder: "https://idp.example.com/.well-known/openid-configuration",
  },
];

export const federationProblemSchema = z.object({
 code: z.string().optional(),
 detail: z.string().optional(),
});
